import { useEffect } from "react";

interface WakeLockSentinelLike {
  readonly released: boolean;
  release: () => Promise<void>;
  addEventListener?: (type: "release", listener: () => void) => void;
}

interface WakeLockLike {
  request: (type: "screen") => Promise<WakeLockSentinelLike>;
}

const wakeLockApi = (): WakeLockLike | null =>
  (globalThis.navigator as { wakeLock?: WakeLockLike } | undefined)?.wakeLock ??
  null;

/**
 * Keeps the screen on while `active` (a recording is running). A locked
 * phone or a sleeping laptop suspends the microphone, so the recording would
 * stop without the doctor noticing.
 *
 * The browser drops the lock whenever the page is hidden; it is asked for
 * again when the page is visible. Released when `active` turns false (pause,
 * stop, discard) and on unmount. Browsers without the Wake Lock API, or that
 * refuse it (low battery, permissions policy), simply record as before.
 */
export const useScreenWakeLock = (active: boolean): void => {
  useEffect(() => {
    const api = wakeLockApi();
    if (!active || !api) return;
    let disposed = false;
    let sentinel: WakeLockSentinelLike | null = null;
    let pending = false;
    const acquire = async (): Promise<void> => {
      if (disposed || pending || document.visibilityState !== "visible") return;
      if (sentinel && !sentinel.released) return;
      pending = true;
      try {
        const lock = await api.request("screen");
        if (disposed) {
          void lock.release().catch(() => undefined);
          return;
        }
        sentinel = lock;
      } catch {
        // Not allowed right now: recording carries on without it.
      } finally {
        pending = false;
      }
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") void acquire();
    };
    document.addEventListener("visibilitychange", onVisibility);
    void acquire();
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibility);
      const lock = sentinel;
      sentinel = null;
      if (lock && !lock.released) void lock.release().catch(() => undefined);
    };
  }, [active]);
};
